import { useEffect, useState } from 'react';
import { usePapaParse } from 'react-papaparse';
import { Box, Modal, Svg, Text } from '@hopskipdrive/react-components';
import { WarningAmberRounded } from '@mui/icons-material';
import { FileUploadDropzone } from '@rideiq/shared/components';
import { Button } from '@rideiq/shared/components/Button';
import { colorTokens } from '@rideiq/shared/components/variables';
import { useQueryClient } from '@tanstack/react-query';
import languageEncoding from 'detect-file-encoding-and-language';

import { batchUploadCsv } from '../../../api/services/BatchUploadFiles';
import { getDownloadFile } from '../../../utils/helpers/getDownloadFile';
import { BATCH_UPLOAD_LOG_PAGE_1_QUERY_KEY, SHOULD_POLL_UPLOAD_LOG_KEY } from '../UploadsTable/queryKeys';

import { riderValidHeaders } from './utils';

export const UploadCsvFile = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>();
  const [uploadStatus, setUploadStatus] = useState<'success' | 'error' | 'encoding' | undefined>();
  const [errorMessages, setErrorMessages] = useState<string[] | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { readString } = usePapaParse();
  const queryClient = useQueryClient();

  const compareArrays = (a, b) => a && b && a.length === b.length && a.every((element, index) => element === b[index]);

  const handleUploadFile = async (fileToUpload) => {
    setIsLoading(true);
    const fileEncoding = await languageEncoding(fileToUpload[0]);

    readString(fileToUpload[0], {
      worker: false,
      complete: async (results) => {
        if (
          compareArrays(
            riderValidHeaders,
            results.data[0].filter((column) => column !== '')
          ) &&
          fileEncoding.encoding === 'UTF-8'
        ) {
          const response = await batchUploadCsv({
            batchUploadFileType: 'rider',
            file: fileToUpload[0],
          });

          setUploadStatus(response.type);
          if (response.type === 'error') {
            setErrorMessages(response.error.errors);
          } else {
            setErrorMessages(undefined);
            queryClient.setQueryData(SHOULD_POLL_UPLOAD_LOG_KEY, true);
            queryClient.invalidateQueries({ queryKey: BATCH_UPLOAD_LOG_PAGE_1_QUERY_KEY });
          }
        } else if (fileToUpload[0].type === 'text/csv' && fileEncoding.encoding !== 'UTF-8') {
          setUploadStatus('encoding');
        } else {
          setUploadStatus('error');
        }
        setIsLoading(false);
      },
    });
  };

  useEffect(() => {
    !isModalOpen && setUploadStatus(undefined);
  }, [isModalOpen]);

  return (
    <>
      {isModalOpen && (
        <Presentation
          handleUploadFile={handleUploadFile}
          isLoading={isLoading}
          uploadStatus={uploadStatus}
          errorMessages={errorMessages}
          onCancel={() => {
            setIsModalOpen(false);
          }}
          onGoBack={() => {
            setUploadStatus(undefined);
          }}
        />
      )}
      <Button color="secondary" size="medium" onClick={() => setIsModalOpen(true)}>
        Upload CSV
      </Button>
    </>
  );
};

const Presentation = ({ handleUploadFile, isLoading, uploadStatus, errorMessages, onCancel, onGoBack }) => {
  const [fileToUpload, setFileToUpload] = useState<File[] | undefined>(undefined);

  return (
    <Modal open={true} data-testid="riders-upload-file-modal">
      <Modal.Title>Upload CSV</Modal.Title>
      <Modal.Content>
        {!uploadStatus ? (
          <FileUploadDropzone handleAcceptedFiles={setFileToUpload} />
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" px={4}>
            <ModalContent uploadStatus={uploadStatus} errorMessages={errorMessages} />
          </Box>
        )}
      </Modal.Content>
      <Modal.Actions style={{ justifyContent: uploadStatus !== 'success' ? 'space-between' : 'flex-end' }}>
        {!uploadStatus && (
          <DefaultButtons
            disabled={!fileToUpload || isLoading}
            onCancel={onCancel}
            handleUploadFile={() => handleUploadFile(fileToUpload)}
          />
        )}
        {fileToUpload && uploadStatus && (
          <ResultsActionButtons
            uploadStatus={uploadStatus}
            onClose={onCancel}
            onGoBack={() => {
              onGoBack();
              setFileToUpload(undefined);
            }}
          />
        )}
      </Modal.Actions>
    </Modal>
  );
};

const DefaultButtons = ({ disabled, onCancel, handleUploadFile }) => {
  const [isFetchingFileToDownload, setIsFetchingFileToDownload] = useState(false);

  const handleDownloadTemplateFile = async () => {
    setIsFetchingFileToDownload(true);
    await getDownloadFile('template', 'rider');
    setIsFetchingFileToDownload(false);
  };

  return (
    <>
      <Button
        color="secondary"
        size="medium"
        startIcon={<Svg icon="download" width="16px" />}
        disabled={isFetchingFileToDownload}
        onClick={() => handleDownloadTemplateFile()}
      >
        Download Template file
      </Button>
      <Box display="flex" gap="1rem">
        <Button color="tertiary" onClick={onCancel}>
          Cancel
        </Button>
        <Button color="primary" disabled={disabled} onClick={() => handleUploadFile()}>
          Upload
        </Button>
      </Box>
    </>
  );
};

const ResultsActionButtons = ({ uploadStatus, onClose, onGoBack }) => {
  const [isFetchingFileToDownload, setIsFetchingFileToDownload] = useState(false);

  const handleDownloadTemplateFile = async () => {
    setIsFetchingFileToDownload(true);
    await getDownloadFile('template', 'rider');
    setIsFetchingFileToDownload(false);
  };

  return (
    <>
      {uploadStatus !== 'success' && (
        <Button
          color="secondary"
          size="medium"
          startIcon={<Svg icon="download" width="16px" />}
          disabled={isFetchingFileToDownload}
          onClick={() => handleDownloadTemplateFile()}
        >
          Download Template .CSV
        </Button>
      )}
      <Box display="flex" gap="1rem">
        {uploadStatus !== 'success' && (
          <Button color="tertiary" onClick={onGoBack}>
            Go back
          </Button>
        )}
        <Button color="primary" onClick={onClose}>
          Close
        </Button>
      </Box>
    </>
  );
};

const ModalContent = ({ uploadStatus, errorMessages }) => {
  switch (uploadStatus) {
    case 'error':
      return (
        <>
          <WarningAmberRounded fontSize="large" color="warning" />
          {!errorMessages ? (
            <Text variant="bodyS" textColor="secondaryText" align="center" mt={4} fontWeight={300}>
              Your file contains unexpected column headers or is an unexpected file type. Please check your file and try
              again.
            </Text>
          ) : (
            <Text variant="bodyS" textColor="secondaryText" align="center" mt={4} fontWeight={300}>
              Upload failed. Please resolve the following issue(s) and try again.
              <ul style={{ listStyle: 'none' }}>
                {errorMessages.map((errorMessage, index) => (
                  <li key={index}>
                    <Text variant="bodyS" textColor="secondaryText" mt={4} fontWeight={300}>
                      - {errorMessage}
                    </Text>
                  </li>
                ))}
              </ul>
            </Text>
          )}
        </>
      );
    case 'encoding':
      return (
        <>
          <WarningAmberRounded fontSize="large" color="warning" />
          <Text variant="bodyS" textColor="secondaryText" align="center" mt={4} fontWeight={300}>
            Resave your file as &quot;CSV UTF-8&quot; and try again.
          </Text>
        </>
      );
    default:
      return (
        <>
          <Svg icon="check-circle-outlined" width={35} height={35} color={colorTokens.success.main} />
          <Text variant="bodyS" textColor="secondaryText" align="center" mt={4} fontWeight={300}>
            We have received your file and will notify you by email when the process is done, including a summary of
            updated records and possible errors.
          </Text>
          <Text variant="bodyS" textColor="secondaryText" align="center" mt={5} fontWeight={300}>
            You can safely close this dialog now.
          </Text>
        </>
      );
  }
};
